import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { useMagnetic } from '@/hooks/useEffects';

function TypeWriter({ texts, speed = 60, pause = 2000 }) {
  const [display, setDisplay] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[textIndex];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplay(current.slice(0, charIndex + 1));
        if (charIndex + 1 === current.length) {
          setTimeout(() => setDeleting(true), pause);
        } else {
          setCharIndex(c => c + 1);
        }
      } else {
        setDisplay(current.slice(0, charIndex - 1));
        if (charIndex <= 1) {
          setDeleting(false);
          setCharIndex(0);
          setTextIndex((textIndex + 1) % texts.length);
        } else {
          setCharIndex(c => c - 1);
        }
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, textIndex, texts, speed, pause]);

  return <>{display}<span className="cursor-blink text-[#00E5FF]">|</span></>;
}

function AnimatedCounter({ end, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const duration = 1200;
        const startTime = performance.now();
        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * end));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

export default function HeroSection() {
  const magnetic = useMagnetic(0.3);
  const heroRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) setOffset(window.scrollY * 0.15);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={heroRef} id="hero" data-testid="hero-section" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#F8F9FA] to-white dark:from-[#0A0A0A] dark:to-[#0A0A0A] pointer-events-none" />
      <div className="absolute top-10 right-[10%] w-[500px] h-[500px] bg-[#00E5FF]/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-[5%] w-[300px] h-[300px] bg-[#00E5FF]/4 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="max-w-xl">
            <ScrollReveal>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-8 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
                Web Design Studio
              </span>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-[#0A0A0A] dark:text-white mb-6">
                Beautiful Websites
                <br />
                That Help Your
                <br />
                <span className="text-gradient-shimmer">Business Grow</span>
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-2 max-w-md h-14">
                <TypeWriter
                  texts={[
                    'Modern websites that attract more customers.',
                    'Designs that make your business stand out.',
                    'Fast, professional, built to convert.',
                  ]}
                  speed={50}
                  pause={2500}
                />
              </p>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="flex flex-wrap gap-4 mt-8">
                <div {...magnetic} ref={magnetic.ref}>
                  <Link
                    to="/pricing"
                    data-testid="hero-cta-primary"
                    className="magnetic-btn group inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-8 py-4 rounded-full font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400 pulse-glow hover:scale-[1.02]"
                  >
                    Build My Quote
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <Link
                  to="/admin-demo"
                  data-testid="hero-cta-secondary"
                  className="group inline-flex items-center gap-2 bg-white dark:bg-white/5 text-black dark:text-white border border-slate-200 dark:border-white/10 px-8 py-4 rounded-full font-medium transition-all hover:border-[#0A0A0A] dark:hover:border-[#00E5FF] hover:shadow-lg"
                >
                  Try the Admin Live
                  <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-100 dark:border-white/10">
                <div className="count-up">
                  <span className="font-['Outfit'] text-2xl font-black text-[#0A0A0A] dark:text-white">
                    <AnimatedCounter end={24} suffix="hr" />
                  </span>
                  <p className="text-xs text-slate-400 mt-0.5">Response Time</p>
                </div>
                <div className="w-px h-10 bg-slate-200 dark:bg-white/10" />
                <div className="count-up">
                  <span className="font-['Outfit'] text-2xl font-black text-[#0A0A0A] dark:text-white">
                    <AnimatedCounter end={100} suffix="%" />
                  </span>
                  <p className="text-xs text-slate-400 mt-0.5">Custom Design</p>
                </div>
                <div className="w-px h-10 bg-slate-200 dark:bg-white/10" />
                <div className="count-up">
                  <span className="font-['Outfit'] text-2xl font-black text-[#00E5FF]">Fair</span>
                  <p className="text-xs text-slate-400 mt-0.5">Honest Pricing</p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right - Mockup with parallax */}
          <ScrollReveal delay={200} className="hidden lg:block">
            <div className="relative" style={{ transform: `translateY(${offset}px)` }}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/10 to-transparent rounded-3xl blur-3xl scale-110 pointer-events-none" />
              <div className="float-animation bg-white dark:bg-[#151515] rounded-2xl shadow-2xl shadow-slate-200/60 dark:shadow-black/40 overflow-hidden border border-slate-200/80 dark:border-white/10 relative">
                <div className="flex items-center gap-2 px-4 py-3 bg-[#FAFBFC] dark:bg-[#111111] border-b border-slate-100 dark:border-white/5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <div className="flex-1 mx-4">
                    <div className="bg-white dark:bg-white/10 rounded-full px-4 py-1 text-[10px] text-slate-400 border border-slate-100 dark:border-white/5 w-fit">
                      www.yourbusiness.com
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div className="h-28 bg-gradient-to-br from-[#0A0A0A] to-slate-700 rounded-xl flex items-end p-4 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#00E5FF]/20 rounded-full blur-2xl" />
                    <div className="space-y-1.5 relative">
                      <div className="h-3 bg-white/90 rounded-full w-32" />
                      <div className="h-2 bg-white/40 rounded-full w-20" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-16 bg-slate-50 dark:bg-white/5 rounded-lg border border-slate-100 dark:border-white/5 p-2.5">
                      <div className="h-2 bg-slate-200 dark:bg-white/20 rounded-full w-full mb-2" />
                      <div className="h-1.5 bg-slate-100 dark:bg-white/10 rounded-full w-3/4" />
                    </div>
                    <div className="h-16 bg-slate-50 dark:bg-white/5 rounded-lg border border-slate-100 dark:border-white/5 p-2.5">
                      <div className="h-2 bg-slate-200 dark:bg-white/20 rounded-full w-full mb-2" />
                      <div className="h-1.5 bg-slate-100 dark:bg-white/10 rounded-full w-3/4" />
                    </div>
                    <div className="h-16 bg-[#00E5FF]/10 rounded-lg border border-[#00E5FF]/20 p-2.5">
                      <div className="h-2 bg-[#00E5FF]/30 rounded-full w-full mb-2" />
                      <div className="h-1.5 bg-[#00E5FF]/15 rounded-full w-3/4" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-slate-100 dark:bg-white/5 rounded-full w-full" />
                    <div className="h-2 bg-slate-100 dark:bg-white/5 rounded-full w-4/5" />
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-10 w-28 float-phone">
                <div className="bg-white dark:bg-[#151515] rounded-2xl shadow-xl border border-slate-200/80 dark:border-white/10 overflow-hidden">
                  <div className="p-2">
                    <div className="h-4 bg-[#0A0A0A] rounded-t-lg" />
                    <div className="h-2 bg-[#00E5FF]/20 mt-1 rounded-sm" />
                    <div className="space-y-1 mt-2">
                      <div className="h-1 bg-slate-100 dark:bg-white/10 rounded-full" />
                      <div className="h-1 bg-slate-100 dark:bg-white/10 rounded-full w-4/5" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-[#0A0A0A] text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                Mobile-Ready
              </div>
              <div className="absolute top-1/2 -right-8 bg-white dark:bg-[#151515] text-[#0A0A0A] dark:text-white px-3 py-1.5 rounded-full text-[10px] font-bold shadow-lg border border-slate-100 dark:border-white/10 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Fast Loading
              </div>
            </div>
          </ScrollReveal>
        </div>

        <div className="hidden md:flex justify-center mt-20">
          <button
            data-testid="scroll-indicator"
            onClick={() => scrollTo('#trust')}
            className="flex flex-col items-center gap-2 text-slate-400 hover:text-[#00E5FF] transition-colors"
          >
            <span className="text-xs font-medium tracking-wider uppercase">Scroll</span>
            <ArrowDown className="w-4 h-4 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
}
